import JSZip from 'jszip';
import * as XLSX from 'xlsx';

export type CandidateAllFieldsReport = {
  title: string;
  fields: string[];
  labels: string[];
  rows: Array<{ cells: Array<{ key: string; value: string | null }> }>;
};

export const buildCandidateAllFieldsReportWorkbook = async (report: CandidateAllFieldsReport, sheetName: string): Promise<ArrayBuffer> => {
  const sheet = XLSX.utils.aoa_to_sheet([
    [report.title], [], report.labels,
    ...report.rows.map((row) => {
      const cells = Object.fromEntries(row.cells.map((cell) => [cell.key, cell.value]));
      return report.fields.map((field) => cells[field] ?? '');
    }),
  ]);
  sheet['!cols'] = report.fields.map(() => ({ wch: 30 }));
  sheet['!merges'] = [XLSX.utils.decode_range(`A1:${XLSX.utils.encode_col(report.fields.length - 1)}1`)];
  const workbook = XLSX.utils.book_new();
  workbook.Props = { Title: report.title, Author: 'LA County Diversion' };
  XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
  return serializeCandidateReportWorkbook(workbook, '');
};

export const serializeCandidateReportWorkbook = async (workbook: XLSX.WorkBook, password: string): Promise<ArrayBuffer> => {
  const bytes: ArrayBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const archive = await JSZip.loadAsync(bytes);
  const sheetFile = archive.file('xl/worksheets/sheet1.xml');
  const workbookFile = archive.file('xl/workbook.xml');
  if (!sheetFile || !workbookFile) throw new Error('The report could not be created.');

  const sheetXml = await sheetFile.async('string');
  archive.file('xl/worksheets/sheet1.xml', sheetXml.replace(
    '<sheetView workbookViewId="0"/>',
    '<sheetView workbookViewId="0"><pane ySplit="3" topLeftCell="A4" activePane="bottomLeft" state="frozen"/></sheetView>',
  ));
  if (password) {
    // SheetJS writes sheet protection but omits workbook structure protection.
    const hash = sheetXml.match(/<sheetProtection\b[^>]*\bpassword="([A-F0-9]+)"/);
    if (!hash) throw new Error('The report could not be protected.');
    const workbookXml = await workbookFile.async('string');
    archive.file('xl/workbook.xml', workbookXml.replace(
      '<sheets>',
      `<workbookProtection workbookPassword="${hash[1]}" lockStructure="1" lockWindows="1"/><sheets>`,
    ));
  }
  return archive.generateAsync({ type: 'arraybuffer' });
};
